/** Shared contracts for the video export pipeline. */

/**
 * A video from any of the three wipe-recording modes, or a single still
 * frame (the "Still image" export, for print handouts, website galleries,
 * or a single feed post). A canvas-produced PNG carries no metadata on its
 * own, so the still path needs none of the video pipeline's explicit
 * metadata-stripping flags.
 */
export interface ExportResult {
  blob: Blob;
  mimeType: 'video/mp4' | 'image/png';
  ext: 'mp4' | 'png';
}

/**
 * A deterministic description of the divider's motion. Every export mode
 * (auto sweep, manual drag, custom pattern) reduces to one of these, so the
 * encoder never has to capture anything live.
 */
export interface Timeline {
  durationMs: number;
  /** Divider x in canvas pixels at time t (ms). Must accept t in [0, durationMs]. */
  xAt(tMs: number): number;
}

/**
 * One scene played through one timeline. A single-set export is one segment;
 * a multi-angle export is several, played back to back in one MP4 — each
 * angle gets its own images/transforms but the same divider motion shape.
 */
export interface ExportSegment<Scene> {
  scene: Scene;
  timeline: Timeline;
}

export type ExportPhase = 'loading' | 'rendering' | 'encoding' | 'finalizing';

export interface ExportProgress {
  phase: ExportPhase;
  /** Overall 0..1 across rendering + encoding. */
  fraction: number;
  rendered: number;
  encoded: number;
  total: number;
}

export class ExportCancelledError extends Error {
  constructor() {
    super('Export cancelled');
    this.name = 'ExportCancelledError';
  }
}
